import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Facebook,
  Youtube,
  Instagram,
  Heart,
  ArrowUp,
  Send,
  Sparkles,
  CheckCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { useSite } from '../../context/SiteContext'
import { useState } from 'react'
import { useNewsletterForm } from '../../hooks/useForm'

const Footer = () => {
  const { settings, getFullAddress, getGoogleMapsUrl } = useSite()
  const [email, setEmail] = useState('')
  const { submit, submitting, submitted, error, reset } = useNewsletterForm()

  const handleSubscribe = async (e) => {
    e.preventDefault()
    if (!email.trim()) return

    const result = await submit(email.trim())
    if (result.success) {
      setEmail('')
      // Auto-reset success message after 5 seconds
      setTimeout(() => reset(), 5000)
    }
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Ministries', path: '/ministries' },
    { name: 'Sermons', path: '/sermons' },
    { name: 'Events', path: '/events' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ]

  const connectLinks = [
    { name: "I'm New Here", path: '/new-here' },
    { name: 'Plan Your Visit', path: '/plan-your-visit' },
    { name: 'Connect Card', path: '/connect' },
    { name: 'Prayer Request', path: '/contact#prayer' },
    { name: 'Give Online', path: 'https://graceledger-production.up.railway.app/give', external: true },
    { name: 'Workers Portal', path: '/workers' },
  ]

  return (
    <footer className="relative overflow-hidden">
      {/* Gradient Top Border */}
      <div className="h-1 bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500" />

      {/* Newsletter Section */}
      <div className="bg-gradient-to-br from-dark-100 to-dark-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-primary-500/10 to-secondary-500/10 backdrop-blur-sm rounded-3xl border border-white/10 p-8 md:p-12"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <span className="inline-flex items-center gap-2 text-primary-400 font-semibold text-sm mb-2">
                  <Sparkles className="w-4 h-4" />
                  Stay Connected
                </span>
                <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-2">
                  Join Our Community
                </h3>
                <p className="text-white/60 max-w-md">
                  Get updates on events, sermons, and more delivered to your inbox.
                </p>
              </div>
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="flex items-center gap-3 bg-emerald-500/20 border border-emerald-500/30 rounded-2xl px-6 py-4"
                  >
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                    <div>
                      <p className="text-white font-semibold">You're subscribed!</p>
                      <p className="text-white/60 text-sm">Thank you for joining our community.</p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={handleSubscribe}
                    className="flex flex-col sm:flex-row gap-3 w-full md:w-auto"
                  >
                    <div className="relative">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={submitting}
                        className="px-6 py-4 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 w-full sm:w-72 disabled:opacity-50"
                      />
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="absolute -bottom-8 left-0 flex items-center gap-1 text-red-400 text-sm"
                        >
                          <AlertCircle className="w-4 h-4" />
                          <span>Something went wrong. Try again.</span>
                        </motion.div>
                      )}
                    </div>
                    <motion.button
                      type="submit"
                      disabled={submitting || !email.trim()}
                      whileHover={{ scale: submitting ? 1 : 1.05 }}
                      whileTap={{ scale: submitting ? 1 : 0.95 }}
                      className="px-8 py-4 rounded-2xl bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Joining...
                        </>
                      ) : (
                        <>
                          Subscribe
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </motion.button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="bg-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* About Column */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="/images/logo.png"
                  alt="DLBC Clarksville TN"
                  className="h-14 w-auto bg-white rounded-2xl p-1"
                />
                <div>
                  <span className="font-heading font-bold text-lg bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                    DLBC Clarksville TN
                  </span>
                  <span className="block text-xs font-accent text-white/50 flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-accent-400" />
                    A Disciple Making Church
                  </span>
                </div>
              </div>
              <p className="text-white/60 mb-6 leading-relaxed">
                A place of worship, growth, and community. Join us as we experience God's
                transforming power together.
              </p>
              <div className="flex gap-3">
                {settings.socialMedia?.facebook && (
                  <motion.a
                    whileHover={{ scale: 1.1, y: -2 }}
                    href={settings.socialMedia.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/5 hover:bg-primary-500 rounded-xl flex items-center justify-center transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                  </motion.a>
                )}
                {settings.socialMedia?.youtube && (
                  <motion.a
                    whileHover={{ scale: 1.1, y: -2 }}
                    href={settings.socialMedia.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/5 hover:bg-red-500 rounded-xl flex items-center justify-center transition-colors"
                  >
                    <Youtube className="w-5 h-5" />
                  </motion.a>
                )}
                {settings.socialMedia?.instagram && (
                  <motion.a
                    whileHover={{ scale: 1.1, y: -2 }}
                    href={settings.socialMedia.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 bg-white/5 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 rounded-xl flex items-center justify-center transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                  </motion.a>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-heading font-bold text-lg mb-6 flex items-center gap-2">
                <span className="w-8 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full" />
                Quick Links
              </h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-white/60 hover:text-white hover:translate-x-1 transition-all inline-block"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h3 className="font-heading font-bold text-lg mb-6 flex items-center gap-2">
                <span className="w-8 h-1 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full" />
                Connect
              </h3>
              <ul className="space-y-3">
                {connectLinks.map((link) => (
                  <li key={link.path}>
                    {link.external ? (
                      <a
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-accent-400 hover:text-accent-300 font-semibold transition-colors"
                      >
                        <Heart className="w-4 h-4 fill-current" />
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.path}
                        className="text-white/60 hover:text-white hover:translate-x-1 transition-all inline-block"
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-heading font-bold text-lg mb-6 flex items-center gap-2">
                <span className="w-8 h-1 bg-gradient-to-r from-accent-500 to-primary-500 rounded-full" />
                Contact Us
              </h3>
              <ul className="space-y-4">
                <li>
                  <a
                    href={getGoogleMapsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 text-white/60 hover:text-white transition-colors group"
                  >
                    <div className="p-2 rounded-lg bg-white/5 group-hover:bg-primary-500/20 transition-colors">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <span className="text-sm">{getFullAddress()}</span>
                  </a>
                </li>
                <li>
                  <a
                    href={`tel:${settings.phone}`}
                    className="flex items-center gap-3 text-white/60 hover:text-white transition-colors group"
                  >
                    <div className="p-2 rounded-lg bg-white/5 group-hover:bg-primary-500/20 transition-colors">
                      <Phone className="w-4 h-4" />
                    </div>
                    <span className="text-sm">{settings.phone}</span>
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${settings.email}`}
                    className="flex items-center gap-3 text-white/60 hover:text-white transition-colors group"
                  >
                    <div className="p-2 rounded-lg bg-white/5 group-hover:bg-primary-500/20 transition-colors">
                      <Mail className="w-4 h-4" />
                    </div>
                    <span className="text-sm">{settings.email}</span>
                  </a>
                </li>
                <li className="flex items-start gap-3 text-white/60">
                  <div className="p-2 rounded-lg bg-white/5">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div className="text-sm">
                    <p className="font-semibold text-white mb-1">Service Times</p>
                    {settings.serviceTimes?.slice(0, 3).map((service, idx) => (
                      <p key={idx} className="text-white/50">
                        {service.day}: {service.time}
                      </p>
                    ))}
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-dark-100 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-sm text-center md:text-left">
              © {new Date().getFullYear()} {settings.fullName}. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <motion.button
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.9 }}
                onClick={scrollToTop}
                className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30"
              >
                <ArrowUp className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
